if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();

      // Configurations
      this.priceEl = this.querySelector(".product-price");
      this.optionsEl = this.querySelector(".product-form__options");
      this.addonsEl = this.querySelector(".product-form__add-ons");
      this.stockIndicatorEl = this.querySelector(
        ".product-form__stock-indicator"
      );
      this.productFormEl = this.querySelector('[data-type="add-to-cart-form"]');
      this.productFormIdInputEl =
        this.productFormEl.querySelector('[name="id"]');
      this.klaviyoBackInStockForm = this.closest("section").querySelector(
        "klaviyo-back-in-stock-form"
      );

      // Listeners
      this.updateProductOptions = this.updateProductOptions.bind(this);
      this.updateAddonOptions = this.updateAddonOptions.bind(this);
    }

    connectedCallback() {
      this.initProductOptions();
      this.initAddonOptions();
      this.updateProductOptions();
    }

    initProductOptions() {
      if (!this.optionsEl) return;
      this.optionsEl
        .querySelectorAll(".product-form__option")
        .forEach((fieldset) => {
          const variantButtons = fieldset.querySelectorAll(
            ".variant-button > input"
          );
          variantButtons.forEach((variantInput) => {
            variantInput.addEventListener(
              "change",
              this.updateProductOptions,
              false
            );
          });
        });
    }

    updateProductOptions(e) {
      const productVariantOptions = JSON.parse(
        this.optionsEl.dataset.productOptions
      );

      // Update variant label
      const labelEl = e?.target
        ?.closest("fieldset")
        ?.querySelector("[data-legend]");
      const variantLabel = e?.target?.value;
      if (labelEl) {
        labelEl.textContent = variantLabel;
      }

      // Get selected options
      const selectedOptions = ["", "", ""];
      let productVariantOptionIndex = 0;
      this.optionsEl
        .querySelectorAll(".product-form__option")
        .forEach((options) => {
          options.querySelectorAll("input").forEach((input) => {
            if (input.checked) {
              selectedOptions[productVariantOptionIndex] = input.value;
            }
          });
          productVariantOptionIndex++;
        });

      // Get selected variant from selected options
      const selectedOption = productVariantOptions.filter(
        (item) =>
          item.option1 === selectedOptions[0] &&
          item.option2 === selectedOptions[1] &&
          item.option3 === selectedOptions[2]
      );

      // If the selected options provide a match, update the product info and form
      if (selectedOption.length) {
        //Update product prices
        this.updatePrice(
          selectedOption[0]["price"],
          selectedOption[0]["compare_at_price"]
        );

        // Update stock count
        this.updateStockIndicator(
          selectedOption[0]["inventory_policy"],
          selectedOption[0]["inventory"]
        );

        document
          .querySelectorAll("product-form")
          .forEach((f) => f.handleErrorMessage());

        // Update product form ID input
        this.productFormIdInputEl.value = selectedOption[0]["id"];

        // Update variant descriptions shown on the page
        this.updateVariantDescriptions(selectedOption[0]["id"]);

        // Update the featured product-media image to match the selected variant when using sample mode
        this.updateVariantImage(selectedOption[0]["id"]);

        // Update klaviyo back in stock custom element
        if (this.klaviyoBackInStockForm) {
          this.klaviyoBackInStockForm.dataset.variantId =
            selectedOption[0]["id"];
          this.klaviyoBackInStockForm.dataset.variantAvailable =
            selectedOption[0].available;
        }

        // Update page URL
        window.history.replaceState(
          {},
          "",
          "?variant=" + selectedOption[0]["id"]
        );

        // Enable/Disable Buy Button
        const buyButton = this.productFormEl.querySelector('[type="submit"]');
        if (selectedOption[0]["available"]) {
          if (buyButton.disabled) {
            const availableText = buyButton.dataset.availableText;
            buyButton.querySelector(".btn__content > span").textContent =
              availableText;
            buyButton.classList.remove("product-form__submit--sold-out");
            buyButton.disabled = false;
          }
        } else {
          // Sold out
          const unavailableText = buyButton.dataset.unavailableText;
          buyButton.querySelector(".btn__content > span").textContent =
            unavailableText;
          buyButton.classList.add("product-form__submit--sold-out");
          buyButton.disabled = true;
        }
      }

      // Update colour product URLs
      this.updateOptionUrls(selectedOptions);
      this.disableOutOfStockOptions();
    }

    /**
     * Disables out-of-stock product options by checking variant availability.
     *
     * Iterates over each option group (Color, Size, etc.) and for each input
     * (e.g., swatch or radio button), checks whether any variant matches:
     * - All selected values from previous options, and
     * - The current option value being evaluated.
     *
     * If no such variant is available, it disables the option visually via data-disabled attribute.
     */
    disableOutOfStockOptions() {
      if (!this.optionsEl) return;

      const productVariantOptions = JSON.parse(
        this.optionsEl.dataset.productOptions
      );
      const selectedOptionValues = this.selectedOptionValues;

      const optionFieldsets = this.optionsEl.querySelectorAll(
        ".product-form__option"
      );

      optionFieldsets.forEach((fieldset, optionIndex) => {
        const inputs = fieldset.querySelectorAll("input");

        inputs.forEach((input) => {
          const isAvailable = productVariantOptions.some((variant) => {
            // Check if this variant matches all selected options up to the current optionIndex
            for (let i = 0; i <= optionIndex; i++) {
              const selectedValue =
                i === optionIndex ? input.value : selectedOptionValues[i];
              if (
                selectedValue &&
                variant[`option${i + 1}`] !== selectedValue
              ) {
                return false;
              }
            }
            return variant.available;
          });

          // Update input's disabled state
          input.toggleAttribute("data-disabled", !isAvailable);
        });
      });
    }

    get selectedOptionValues() {
      return Array.from(this.optionsEl.querySelectorAll("fieldset")).map(
        (fieldset) => fieldset.querySelector("input:checked")?.value
      );
    }

    updatePrice(price, compare) {
      if (!this.priceEl) return;
      const singlePriceEl = this.priceEl.querySelector("[data-single-price]");
      const comparePriceEl = this.priceEl.querySelector("[data-compare-price]");

      // Formatting when dealing with currencies, as EUR uses commas for decimals
      function parsePriceWithCurrency(str) {
        if (!str) return 0;
        if (str.includes("€")) {
          let cleaned = str.replace(/[^0-9.,-]+/g, "").trim();
          cleaned = cleaned.replace(/\./g, "").replace(/,/g, ".");
          return parseFloat(cleaned);
        } else {
          let cleaned = str.replace(/[^0-9.,-]+/g, "").trim();
          cleaned = cleaned.replace(/,/g, "");
          return parseFloat(cleaned);
        }
      }

      const compareInt = compare ? parsePriceWithCurrency(compare) : 0;

      if (!compare || compareInt === 0) {
        singlePriceEl.textContent = price.replace(/<[^>]*>?/gm, "");
        singlePriceEl.classList.remove("hidden");
        comparePriceEl.classList.add("hidden");
      } else {
        comparePriceEl.querySelector("[data-price]").textContent =
          price.replace(/<[^>]*>?/gm, "");
        comparePriceEl.querySelector("[data-compare]").textContent =
          compare.replace(/<[^>]*>?/gm, "");

        const newPrice = parsePriceWithCurrency(price);
        const oldPrice = parsePriceWithCurrency(compare);
        const discount =
          oldPrice > 0 ? ((oldPrice - newPrice) * 100) / oldPrice : 0;
        comparePriceEl.querySelector(".badge-xs").textContent =
          Math.round(discount) + "% off";

        singlePriceEl.classList.add("hidden");
        comparePriceEl.classList.remove("hidden");
      }
    }

    updateStockIndicator(policy, quantity) {
      if (!this.stockIndicatorEl) return;
      // Get text and colour from data attributes set in theme settings
      const lowStockFreshold = parseInt(
        this.stockIndicatorEl.dataset.lowStockThreshold
      );
      let aboveThreshold = false;
      let text = "";
      let color = "";
      if (policy == "continue") {
        text = this.stockIndicatorEl.dataset.inStockText;
        color = this.stockIndicatorEl.dataset.inStockColour;
        aboveThreshold = true;
      } else if (quantity <= 0) {
        text = this.stockIndicatorEl.dataset.soldOutText;
        color = this.stockIndicatorEl.dataset.soldOutColour;
      } else if (quantity <= lowStockFreshold) {
        text = this.stockIndicatorEl.dataset.lowStockText.replace(
          "#",
          quantity
        );
        color = this.stockIndicatorEl.dataset.lowStockColour;
      } else {
        text = this.stockIndicatorEl.dataset.inStockText;
        color = this.stockIndicatorEl.dataset.inStockColour;
        aboveThreshold = true;
      }

      // Update stock indicator content and show
      const stockIndicatorTextEl = this.stockIndicatorEl.querySelector(
        "[data-inventory-indicator-text]"
      );
      stockIndicatorTextEl.textContent = text;
      this.stockIndicatorEl.style.setProperty("--stock-color", color);
      if (policy == "deny" && quantity <= 0) {
        this.stockIndicatorEl.classList.add("hidden");
      } else {
        if (
          this.stockIndicatorEl.classList.contains("hidden-when-available") &&
          aboveThreshold
        ) {
          this.stockIndicatorEl.classList.add("hidden");
        } else {
          this.stockIndicatorEl.classList.remove("hidden");
        }
      }
    }

    initAddonOptions() {
      if (!this.addonsEl) return;
      this.addonsEl
        .querySelectorAll(".product-form__add-on")
        .forEach((fieldset) => {
          const variantButtons = fieldset.querySelectorAll(
            ".variant-button > input"
          );
          variantButtons.forEach((variantInput) => {
            variantInput.addEventListener(
              "change",
              this.updateAddonOptions,
              false
            );
          });
        });
    }

    updateAddonOptions(e) {
      // Updates selected labels
      const labelEl = e.target
        .closest("fieldset")
        .querySelector("[data-add-on-variant]");
      const variantLabel = e.target.value;
      labelEl.textContent = variantLabel;

      // Updates add on properties for line items
      const propertyValue = e.target.value;
      const propertyName = e.target.dataset.propertyName;
      document.querySelector(
        '[name="properties[' + propertyName + ']"]'
      ).value = propertyValue;
    }

    /**
     * Show the matching variant description and hide others.
     * If no variantId is passed, uses the current form `id` value.
     * Matches elements with `[data-variant]` inside this component.
     */
    updateVariantDescriptions(variantId) {
      const id = variantId
        ? String(variantId)
        : String(this.productFormIdInputEl?.value || "");
      const descriptions = this.querySelectorAll(
        ".variant-descriptions [data-variant]"
      );
      descriptions.forEach((el) => {
        if (String(el.dataset.variant) === id) {
          el.classList.remove("hidden");
        } else {
          el.classList.add("hidden");
        }
      });
    }

    /**
     * Move the matching featured variant image to be the first slide when `data-sample` is true.
     * Expects slides to have a `data-variant` attribute containing one or more variant ids (CSV).
     */
    updateVariantImage(variantId) {
      if (!variantId) return;
      const sectionEl = this.closest("section");
      if (!sectionEl) return;

      const carousel = sectionEl.querySelector("[data-product-media]");
      if (!carousel) return;

      // Only run when sample mode is enabled on the carousel
      if (String(carousel.dataset.sample) !== "true") return;

      const list = carousel.querySelector(".splide__list");
      if (!list) return;

      const slides = Array.from(list.querySelectorAll(".splide__slide"));

      // Capture original indexes on first run so we can restore natural order
      slides.forEach((slide, idx) => {
        if (
          !slide.dataset.originalIndex &&
          slide.dataset.originalIndex !== "0"
        ) {
          slide.dataset.originalIndex = idx;
        }
      });

      const variantStr = String(variantId);

      const matched = slides.find((slide) => {
        const dataVar =
          slide.getAttribute("data-variants") || slide.dataset.variants;
        if (!dataVar) return false;
        let variants;
        try {
          variants = JSON.parse(dataVar);
        } catch (e) {
          variants = dataVar
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        if (!Array.isArray(variants)) variants = [variants];
        variants = variants.map(String);
        return variants.includes(variantStr);
      });

      // If there's no matching featured image, restore natural order and exit
      if (!matched) {
        const ordered = slides
          .slice()
          .sort(
            (a, b) =>
              Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex)
          );
        const current = Array.from(list.children);
        let same =
          current.length === ordered.length &&
          ordered.every((s, i) => s === current[i]);
        if (!same) {
          const frag = document.createDocumentFragment();
          ordered.forEach((s) => frag.appendChild(s));
          list.innerHTML = "";
          list.appendChild(frag);
          // recompute classes
          Array.from(list.children).forEach((slide, index) => {
            const colFull = "md:col-span-2";
            const colHalf = "md:col-span-1";
            const newClass = index === 0 || index % 3 === 0 ? colFull : colHalf;
            slide.classList.remove(colFull, colHalf);
            slide.classList.add(newClass);
          });
          carousel.dispatchEvent(
            new CustomEvent("variant-image-updated", {
              detail: { variantId: null },
            })
          );
        }
        return;
      }

      // If already first, nothing to do
      if (list.firstElementChild === matched) return;

      // Rebuild the list so matched is first and all other slides follow
      // in their original page-load order (preserving natural positions)
      const slidesByOriginal = slides
        .slice()
        .sort(
          (a, b) =>
            Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex)
        );

      const fragment = document.createDocumentFragment();
      fragment.appendChild(matched);
      slidesByOriginal.forEach((s) => {
        if (s !== matched) fragment.appendChild(s);
      });

      // Replace list contents with new ordering
      list.innerHTML = "";
      list.appendChild(fragment);

      // Recompute layout classes so the first is full-width and following follow the pattern
      Array.from(list.children).forEach((slide, index) => {
        const colFull = "md:col-span-2";
        const colHalf = "md:col-span-1";
        const newClass = index === 0 || index % 3 === 0 ? colFull : colHalf;
        slide.classList.remove(colFull, colHalf);
        slide.classList.add(newClass);
      });

      // Notify carousel component in case it needs to refresh
      carousel.dispatchEvent(
        new CustomEvent("variant-image-updated", { detail: { variantId } })
      );
    }

    /**
     * Updates the URLs for color variant options based on selected product options
     * @param {string[]} selectedOptions - Array of currently selected product options
     */
    updateOptionUrls(selectedOptions) {
      if (!window.location.search.includes("variant")) return;

      // Get all color variant URL elements
      const productColourOptions = document.querySelectorAll(
        ".product-options__colour-url"
      );
      if (!productColourOptions.length) return;

      // Parse the product variations data from the JSON element
      const relatedProductOptions = JSON.parse(
        document.getElementById("product-variations-json").textContent
      );

      const findMatchingVariant = (variants, options) =>
        variants.find((variant) =>
          options.every(
            (option, index) =>
              option === null || variant[`option${index + 1}`] === option
          )
        );

      // Update each color variant URL
      productColourOptions.forEach((colourUrl) => {
        // Extract product ID and base URL from dataset
        const { productId, baseUrl } = colourUrl.dataset;

        // Find the related product from variations data
        const relatedProduct = relatedProductOptions.find(
          (item) => item.id === Number(productId)
        );
        if (!relatedProduct) return;

        // Map empty options to null for consistent comparison
        const mappedOptions = selectedOptions.map((option) => option || null);

        // Find matching variant and update URL
        const selectedVariant = findMatchingVariant(
          relatedProduct.variants,
          mappedOptions
        );
        colourUrl.href = selectedVariant
          ? `${baseUrl}?variant=${selectedVariant.id}`
          : baseUrl;
      });
    }
  }

  customElements.define("product-info", ProductInfo);
}
